import MEDIA_QUERY_SIZE from '../../constants/mediaQuerySizes';
import type { IReaderChapterProgressItem } from '../../interfaces';
import { MOBILE_ANNOTATION_EDITOR_KEYBOARD_THRESHOLD_PX } from '../Editor/editors/helpers';

export type AnnotationEditorPlacement = 'top' | 'bottom';
export interface DockedAnnotationViewportFrame {
    top: number,
    height: number,
}
export interface ReaderPostEditorRenderGate {
    write: boolean,
    hasEditorRootPostId: boolean,
    hasHydratedScope: boolean,
    hasCurrentPostValue: boolean,
    scopeUnlocked: boolean,
    postEditorDeleteTransitionInProgress: boolean,
}
export interface ReaderPostFetchContextGate {
    write: boolean,
    hasAlignedReadAuthContext: boolean,
    fetchedUserAdminStatus: boolean,
}
export interface ReaderPostFileDragMetadata {
    fileCount: number,
    hasSupportedType: boolean | null,
}
export interface ReaderPostFileDragFeedback {
    isActive: boolean,
    hasSuccess: boolean,
    hasFailure: boolean,
    fileCount: number,
}
export interface ReaderPostFileDragFeedbackParams {
    write: boolean,
    isDragActive: boolean,
    isDragAccept: boolean,
    isDragReject: boolean,
    feedbackSuppressed: boolean,
    metadata: ReaderPostFileDragMetadata | null,
}

/**
 * Mirrors the persisted reader-progress shape, minus the history-specific
 * identifier and timestamp that ReaderView resolves at save time.
 */
export interface ReaderProgressLocationSnapshot {
    userId: IReaderChapterProgressItem['userId'],
    readerChapterProgressKey: IReaderChapterProgressItem['readerChapterProgressKey'],
    postId: IReaderChapterProgressItem['postId'],
    chapterId?: IReaderChapterProgressItem['chapterId'],
    sectionId?: IReaderChapterProgressItem['sectionId'],
    scrollTop: IReaderChapterProgressItem['scrollTop'],
    scrollProgress: IReaderChapterProgressItem['scrollProgress'],
    anchorParagraphId?: IReaderChapterProgressItem['anchorParagraphId'],
    anchorParagraphIndex?: IReaderChapterProgressItem['anchorParagraphIndex'],
    anchorParagraphOffsetProgress?: IReaderChapterProgressItem['anchorParagraphOffsetProgress'],
}
export interface IReaderViewportParagraphAnchorCandidate {
    paragraphId?: string,
    paragraphTop: number,
    paragraphHeight: number,
}
export interface IReaderViewportParagraphAnchor {
    anchorParagraphId?: string,
    anchorParagraphIndex: number,
    anchorParagraphOffsetProgress: number,
}

interface HighlightRectLike {
    top: number,
    right: number,
    bottom: number,
    left: number,
}

const DOCUMENT_POSITION_PRECEDING = typeof Node !== 'undefined'
    ? Node.DOCUMENT_POSITION_PRECEDING
    : 2;
const DOCUMENT_POSITION_FOLLOWING = typeof Node !== 'undefined'
    ? Node.DOCUMENT_POSITION_FOLLOWING
    : 4;
const POST_EDITOR_ACCEPTED_FILE_TYPE_PREFIXES = ['image/', 'audio/'];

const fileTypeIsSupportedByPostEditor = (type: string): boolean => (
    POST_EDITOR_ACCEPTED_FILE_TYPE_PREFIXES.some((acceptedPrefix) => type.startsWith(acceptedPrefix))
);

const arrayFromFileListLike = <Item>(items: ArrayLike<Item> | null | undefined): Item[] => {
    if (!items) {
        return [];
    }

    const nextItems: Item[] = [];
    for (let i = 0; i < items.length; i += 1) {
        nextItems.push(items[i]);
    }

    return nextItems;
};

/**
 * Reads browser drag metadata for the post editor file drop affordance.
 * Browsers can hide a dragged file's MIME type until drop, so unknown types are
 * intentionally treated as indeterminate instead of unsupported.
 */
export const resolveReaderPostFileDragMetadata = (
    dataTransfer: DataTransfer | null | undefined,
): ReaderPostFileDragMetadata | null => {
    if (!dataTransfer) {
        return null;
    }

    const itemFiles = arrayFromFileListLike<DataTransferItem>(dataTransfer.items)
        .filter((item) => item.kind === 'file');

    if (itemFiles.length > 0) {
        const itemTypes = itemFiles.map((item) => item.type || '');
        const hasHiddenType = itemTypes.some((type) => type.trim() === '');

        if (hasHiddenType) {
            return {
                fileCount: itemFiles.length,
                hasSupportedType: null,
            };
        }

        return {
            fileCount: itemFiles.length,
            hasSupportedType: itemTypes.every(fileTypeIsSupportedByPostEditor),
        };
    }

    const files = arrayFromFileListLike<File>(dataTransfer.files);
    if (files.length === 0) {
        return null;
    }

    const fileTypes = files.map((file) => file.type || '');
    const hasHiddenType = fileTypes.some((type) => type.trim() === '');
    if (hasHiddenType) {
        return {
            fileCount: files.length,
            hasSupportedType: null,
        };
    }

    return {
        fileCount: files.length,
        hasSupportedType: fileTypes.every(fileTypeIsSupportedByPostEditor),
    };
};

/**
 * Converts dropzone state and browser drag metadata into ReaderView's visible
 * post-editor drag feedback, suppressing false rejections for hidden MIME data.
 */
export const resolveReaderPostFileDragFeedback = ({
    write,
    isDragActive,
    isDragAccept,
    isDragReject,
    feedbackSuppressed,
    metadata,
}: ReaderPostFileDragFeedbackParams): ReaderPostFileDragFeedback => {
    const inactiveFeedback = {
        isActive: false,
        hasSuccess: false,
        hasFailure: false,
        fileCount: 0,
    };

    if (!write || !isDragActive || feedbackSuppressed) {
        return inactiveFeedback;
    }

    if (metadata?.hasSupportedType === false) {
        return {
            isActive: true,
            hasSuccess: false,
            hasFailure: true,
            fileCount: metadata.fileCount,
        };
    }

    if (
        metadata?.hasSupportedType === true
        || metadata?.hasSupportedType === null
        || isDragAccept
    ) {
        return {
            isActive: true,
            hasSuccess: true,
            hasFailure: false,
            fileCount: metadata?.fileCount || 0,
        };
    }

    if (isDragReject) {
        return {
            isActive: true,
            hasSuccess: false,
            hasFailure: true,
            fileCount: metadata?.fileCount || 0,
        };
    }

    return inactiveFeedback;
};

export const resolveReaderPostFileDragFeedbackKey = (
    feedback: ReaderPostFileDragFeedback,
): string | null => {
    if (!feedback.isActive) {
        return null;
    }

    const status = feedback.hasFailure ? 'failure' : 'success';
    return `${status}:${feedback.fileCount}`;
};

export const resolvePostBannerRightContainerWidth = ({
    annotationsHidden,
    viewportWidth,
    annotationViewWidth,
    showStickyPurchaseButtons,
    stickyPurchaseButtonCount,
    stickyPurchaseButtonWidth,
    stickyPurchaseButtonGap,
    stickyPurchaseButtonRightOffset,
    hiddenAnnotationsBufferWidth,
    hiddenAnnotationsBufferWidthSmall,
    smallAnnotationViewWidth,
}: {
    annotationsHidden: boolean,
    viewportWidth: number,
    annotationViewWidth: number,
    showStickyPurchaseButtons: boolean,
    stickyPurchaseButtonCount: number,
    stickyPurchaseButtonWidth: number,
    stickyPurchaseButtonGap: number,
    stickyPurchaseButtonRightOffset: number,
    hiddenAnnotationsBufferWidth: number,
    hiddenAnnotationsBufferWidthSmall: number,
    smallAnnotationViewWidth: number,
}): number => {
    if (annotationsHidden) {
        const gutterWidth = viewportWidth >= MEDIA_QUERY_SIZE.small.min
            ? hiddenAnnotationsBufferWidth
            : hiddenAnnotationsBufferWidthSmall;

        if (!showStickyPurchaseButtons || stickyPurchaseButtonCount <= 0) {
            return gutterWidth;
        }

        const stickyPurchaseButtonsGapWidth = Math.max(
            stickyPurchaseButtonCount - 1,
            0,
        ) * stickyPurchaseButtonGap;
        const stickyPurchaseButtonsWidth = (
            stickyPurchaseButtonCount * stickyPurchaseButtonWidth
        ) + stickyPurchaseButtonsGapWidth + stickyPurchaseButtonRightOffset;

        return Math.max(
            gutterWidth,
            stickyPurchaseButtonsWidth,
        );
    }

    if (viewportWidth >= MEDIA_QUERY_SIZE.medium.min) {
        return annotationViewWidth;
    }

    return smallAnnotationViewWidth;
};

/**
 * Write mode should keep the editor mounted once the selected scope is ready so
 * admin/session refreshes do not bounce the UI back to the placeholder. Read
 * mode still waits for body content because its placeholder handoff depends on
 * fetched content and progressive rendering state.
 */
export const canRenderReaderPostEditor = ({
    write,
    hasEditorRootPostId,
    hasHydratedScope,
    hasCurrentPostValue,
    scopeUnlocked,
    postEditorDeleteTransitionInProgress,
}: ReaderPostEditorRenderGate): boolean => {
    if (postEditorDeleteTransitionInProgress) {
        return false;
    }

    if (!hasEditorRootPostId || !hasHydratedScope || !scopeUnlocked) {
        return false;
    }

    if (write) {
        return true;
    }

    return hasCurrentPostValue;
};

/**
 * Write mode only needs resolved admin status to bootstrap the editor payload.
 * Session creation can complete in parallel and should not leave the authoring
 * surface parked on the placeholder when the post data itself is ready.
 */
export const hasRequiredReaderPostFetchContext = ({
    write,
    hasAlignedReadAuthContext,
    fetchedUserAdminStatus,
}: ReaderPostFetchContextGate): boolean => {
    if (write) {
        return fetchedUserAdminStatus;
    }

    return hasAlignedReadAuthContext;
};

export const resolveAnnotationEditorPlacement = ({
    isMultiLineSelection,
    selectionIsBackward,
}: {
    isMultiLineSelection: boolean,
    selectionIsBackward: boolean,
}): AnnotationEditorPlacement => {
    if (!isMultiLineSelection) {
        return 'top';
    }

    return selectionIsBackward ? 'top' : 'bottom';
};

/**
 * Resolves the paragraph currently aligned with the reader's anchor viewport
 * position so progress saves and cart clicks share the same paragraph target.
 */
export const resolveReaderViewportParagraphAnchor = ({
    anchorScrollTop,
    paragraphs,
}: {
    anchorScrollTop: number,
    paragraphs: IReaderViewportParagraphAnchorCandidate[],
}): IReaderViewportParagraphAnchor | null => {
    if (paragraphs.length === 0) {
        return null;
    }

    let anchorParagraphIndex = 0;
    for (let i = 0; i < paragraphs.length; i += 1) {
        if (paragraphs[i].paragraphTop <= anchorScrollTop) {
            anchorParagraphIndex = i;
        } else {
            break;
        }
    }

    const anchorParagraph = paragraphs[anchorParagraphIndex];
    const normalizedParagraphId = `${anchorParagraph.paragraphId || ''}`.trim() || undefined;
    const normalizedParagraphHeight = Math.max(anchorParagraph.paragraphHeight, 1);

    return {
        anchorParagraphId: normalizedParagraphId,
        anchorParagraphIndex,
        anchorParagraphOffsetProgress: Math.min(
            1,
            Math.max(
                0,
                (anchorScrollTop - anchorParagraph.paragraphTop) / normalizedParagraphHeight,
            ),
        ),
    };
};

/**
 * Determines whether a rendered selection spans multiple visual lines. ReaderView
 * uses the same rule for the live DOM selection and the painted highlight spans
 * so the desktop annotation editor can preserve its original dock edge after the
 * quote decorators replace the browser selection.
 */
export const resolveSelectionSpansMultipleLines = (
    rects: HighlightRectLike[],
): boolean => {
    if (rects.length <= 1) {
        return false;
    }

    const firstRect = rects[0];
    const lastRect = rects[rects.length - 1];
    return Math.abs(lastRect.top - firstRect.top) > 1
        || Math.abs(lastRect.bottom - firstRect.bottom) > 1;
};

/**
 * Determines whether a DOM selection was made backwards so ReaderView can keep
 * desktop annotation placement aligned with the drag direction. Bitmask checks
 * are required here because compareDocumentPosition can combine ordering flags
 * with containment flags for nested text nodes.
 */
export const resolveSelectionIsBackward = ({
    anchorNode,
    focusNode,
    anchorOffset,
    focusOffset,
    rangeStartNode,
    rangeStartOffset,
    rangeEndNode,
    rangeEndOffset,
    documentRef,
}: {
    anchorNode: Node | null,
    focusNode: Node | null,
    anchorOffset: number,
    focusOffset: number,
    rangeStartNode?: Node | null,
    rangeStartOffset?: number,
    rangeEndNode?: Node | null,
    rangeEndOffset?: number,
    documentRef?: Document,
}): boolean => {
    if (!anchorNode || !focusNode) {
        return false;
    }

    if (anchorNode === focusNode) {
        return anchorOffset > focusOffset;
    }

    /**
     * DOM Ranges are always normalized to document order, so comparing the
     * browser selection anchor/focus against the live range endpoints is the
     * most reliable way to tell whether the user dragged backward.
     */
    if (
        rangeStartNode
        && rangeEndNode
        && typeof rangeStartOffset === 'number'
        && typeof rangeEndOffset === 'number'
    ) {
        if (
            anchorNode === rangeEndNode
            && focusNode === rangeStartNode
            && anchorOffset === rangeEndOffset
            && focusOffset === rangeStartOffset
        ) {
            return true;
        }

        if (
            anchorNode === rangeStartNode
            && focusNode === rangeEndNode
            && anchorOffset === rangeStartOffset
            && focusOffset === rangeEndOffset
        ) {
            return false;
        }
    }

    const position = anchorNode.compareDocumentPosition(focusNode);
    // compareDocumentPosition is a bitmask; combined ordering/containment flags are expected.
    // eslint-disable-next-line no-bitwise
    if (position & DOCUMENT_POSITION_PRECEDING) {
        return true;
    }
    // compareDocumentPosition is a bitmask; combined ordering/containment flags are expected.
    // eslint-disable-next-line no-bitwise
    if (position & DOCUMENT_POSITION_FOLLOWING) {
        return false;
    }

    const ownerDocument = documentRef || anchorNode.ownerDocument || focusNode.ownerDocument;
    if (!ownerDocument) {
        return false;
    }

    const range = ownerDocument.createRange();
    range.setStart(anchorNode, anchorOffset);
    range.setEnd(focusNode, focusOffset);
    return range.collapsed;
};

/**
 * Small reader layouts dock the annotation editor to the viewport edge instead
 * of following the highlight geometry while the page scrolls.
 */
export const shouldDockAnnotationEditorToViewport = (viewportWidth: number): boolean => (
    viewportWidth < MEDIA_QUERY_SIZE.medium.min
);

export const resolveHighlightSelectionRect = (
    rects: HighlightRectLike[],
): (
    HighlightRectLike & { width: number, height: number }
) | null => {
    if (rects.length === 0) {
        return null;
    }

    const top = Math.min(...rects.map((rect) => rect.top));
    const right = Math.max(...rects.map((rect) => rect.right));
    const bottom = Math.max(...rects.map((rect) => rect.bottom));
    const left = Math.min(...rects.map((rect) => rect.left));

    return {
        top,
        right,
        bottom,
        left,
        width: Math.max(right - left, 0),
        height: Math.max(bottom - top, 0),
    };
};

/**
 * Aligns the top edge of a highlighted selection with the visible reader viewport
 * when the docked mobile annotation editor opens. Touch browser chrome can
 * shift the visual viewport downward, so we subtract that offset to keep the
 * highlighted text aligned with the visible top edge instead of overscrolling.
 */
export const resolveDockedAnnotationEditorRevealScrollTop = ({
    currentScrollTop,
    highlightTop,
    scrollSurfaceTop,
    topViewportInset,
    visualViewportOffsetTop,
}: {
    currentScrollTop: number,
    highlightTop: number,
    scrollSurfaceTop: number,
    topViewportInset: number,
    visualViewportOffsetTop: number,
}): number => Math.max(
    0,
    currentScrollTop
    + (highlightTop - scrollSurfaceTop)
    - topViewportInset
    - visualViewportOffsetTop,
);

/**
 * Reader progress history should only branch when the saved reader position
 * changes. Browser lifecycle events such as `visibilitychange`, `beforeunload`,
 * and React cleanup can all force-flush the same location in quick succession,
 * so ReaderView reuses the last history id while still refreshing the timestamp.
 */
export const readerProgressSnapshotsMatch = ({
    first,
    second,
}: {
    first: ReaderProgressLocationSnapshot,
    second: ReaderProgressLocationSnapshot,
}): boolean => (
    first.userId === second.userId
    && first.readerChapterProgressKey === second.readerChapterProgressKey
    && first.postId === second.postId
    && first.chapterId === second.chapterId
    && first.sectionId === second.sectionId
    && first.scrollTop === second.scrollTop
    && first.scrollProgress === second.scrollProgress
    && first.anchorParagraphId === second.anchorParagraphId
    && first.anchorParagraphIndex === second.anchorParagraphIndex
    && first.anchorParagraphOffsetProgress === second.anchorParagraphOffsetProgress
);

/**
 * Reuses the previous history identifier when ReaderView is persisting the same
 * logical chapter position again. This keeps the latest timestamp while making
 * repeated forced flushes idempotent in the history collection.
 */
export const resolveReaderProgressHistoryIdentity = ({
    previousProgress,
    nextProgress,
    nextTimestamp,
    createId,
}: {
    previousProgress?: IReaderChapterProgressItem | null,
    nextProgress: ReaderProgressLocationSnapshot,
    nextTimestamp: number,
    createId: () => string,
}): Pick<IReaderChapterProgressItem, 'id' | 'timestamp'> => {
    const shouldReuseHistoryId = previousProgress != null
        && readerProgressSnapshotsMatch({
            first: previousProgress,
            second: nextProgress,
        });

    if (shouldReuseHistoryId) {
        return {
            id: previousProgress.id,
            timestamp: nextTimestamp,
        };
    }

    return {
        id: createId(),
        timestamp: nextTimestamp,
    };
};

/**
 * Normalizes the visible viewport frame that the docked touch-mobile
 * annotation editor should use as its fixed overlay bounds.
 */
export const resolveDockedAnnotationViewportFrame = ({
    layoutViewportHeight,
    visualViewportHeight,
    visualViewportOffsetTop,
}: {
    layoutViewportHeight: number,
    visualViewportHeight: number,
    visualViewportOffsetTop: number,
}): DockedAnnotationViewportFrame => {
    const top = Math.max(visualViewportOffsetTop, 0);
    return {
        top,
        height: Math.max(
            Math.min(visualViewportHeight, Math.max(layoutViewportHeight - top, 0)),
            0,
        ),
    };
};

/**
 * Measures how much of the layout viewport is occluded beneath a docked
 * touch-mobile overlay frame.
 */
export const resolveDockedAnnotationViewportBottomGap = ({
    frame,
    layoutViewportHeight,
}: {
    frame: DockedAnnotationViewportFrame,
    layoutViewportHeight: number,
}): number => Math.max(
    layoutViewportHeight - (frame.top + frame.height),
    0,
);

/**
 * During the keyboard-opening burst, keep the most-occluded viewport frame so
 * the docked annotation editor never starts too low and then corrects upward
 * after the user begins scrolling.
 */
export const resolvePreferredDockedAnnotationViewportFrame = ({
    currentFrame,
    nextFrame,
    layoutViewportHeight,
}: {
    currentFrame: DockedAnnotationViewportFrame | null,
    nextFrame: DockedAnnotationViewportFrame,
    layoutViewportHeight: number,
}): DockedAnnotationViewportFrame => {
    if (!currentFrame) {
        return nextFrame;
    }

    const currentBottomGap = resolveDockedAnnotationViewportBottomGap({
        frame: currentFrame,
        layoutViewportHeight,
    });
    const nextBottomGap = resolveDockedAnnotationViewportBottomGap({
        frame: nextFrame,
        layoutViewportHeight,
    });

    return nextBottomGap >= currentBottomGap
        ? nextFrame
        : currentFrame;
};

/**
 * Once the mobile keyboard is open, keep the captured keyboard-safe frame
 * fixed for the rest of the visible editor session. The docked editor should
 * not chase scroll-time visual-viewport movement at all; that is the jitter
 * users were seeing before the dock finally settled.
 */
export const resolveAppliedDockedAnnotationViewportFrame = ({
    capturedFrame,
    liveFrame,
    layoutViewportHeight,
}: {
    capturedFrame: DockedAnnotationViewportFrame | null,
    liveFrame: DockedAnnotationViewportFrame,
    layoutViewportHeight: number,
}): DockedAnnotationViewportFrame => {
    if (
        !capturedFrame
        || !frameHasDockedAnnotationKeyboardOcclusion({
            frame: capturedFrame,
            layoutViewportHeight,
        })
    ) {
        return liveFrame;
    }

    return capturedFrame;
};

/**
 * Once the dock hook is driven only from real visual-viewport events, the
 * overlay should follow the reported top inset directly. Holding small deltas
 * here causes the editor to appear stuck for a beat and then jump into place
 * at scroll startup.
 */
export const resolveDockedAnnotationViewportTop = ({
    nextTop,
}: {
    nextTop: number,
}): number => nextTop;

/**
 * A focused docked editor should keep its captured keyboard-safe frame even if
 * scroll-time viewport samples briefly look like the keyboard disappeared.
 * The frame should only be released once focus leaves the editor.
 */
export const shouldRetainCapturedDockedAnnotationViewportFrame = ({
    capturedFrame,
    liveFrame,
    layoutViewportHeight,
    editorHasFocus,
}: {
    capturedFrame: DockedAnnotationViewportFrame | null,
    liveFrame: DockedAnnotationViewportFrame,
    layoutViewportHeight: number,
    editorHasFocus: boolean,
}): boolean => (
    !!capturedFrame
    && editorHasFocus
    && frameHasDockedAnnotationKeyboardOcclusion({
        frame: capturedFrame,
        layoutViewportHeight,
    })
    && !frameHasDockedAnnotationKeyboardOcclusion({
        frame: liveFrame,
        layoutViewportHeight,
    })
);

/**
 * Identifies whether a docked touch-mobile viewport frame reflects a real
 * keyboard occlusion rather than ordinary browser-chrome movement.
 */
export const frameHasDockedAnnotationKeyboardOcclusion = ({
    frame,
    layoutViewportHeight,
    minimumKeyboardOffset = MOBILE_ANNOTATION_EDITOR_KEYBOARD_THRESHOLD_PX,
}: {
    frame: DockedAnnotationViewportFrame,
    layoutViewportHeight: number,
    minimumKeyboardOffset?: number,
}): boolean => (
    resolveDockedAnnotationViewportBottomGap({
        frame,
        layoutViewportHeight,
    }) >= minimumKeyboardOffset
);
